export type AclPermission = [string]; // ['feedback.create']
export type AclParametrizedPermission = [string, string]; // ['feedback.create', '123']
export type AclFactory = {
  (id?: string): AclPermission | AclParametrizedPermission;
  permission: AclPermission;
};

export type AclLeafExpression =
  | AclPermission
  | AclParametrizedPermission
  | AclFactory;
export type AclQuery = ['&&' | '||' | '!', ...AclExpression[]];
export type AclExpression = AclQuery | AclLeafExpression;

export type SerializableAclLeafExpression =
  | AclPermission
  | AclParametrizedPermission;
export type SerializableAclQuery = [
  '&&' | '||' | '!',
  ...SerializableAclExpression[],
];
export type SerializableAclExpression =
  | SerializableAclQuery
  | SerializableAclLeafExpression;

export function makeSerializableAclExpression(
  expression: AclExpression,
): SerializableAclExpression {
  if (Array.isArray(expression)) {
    const elements: any[] = expression;
    return elements.map(
      makeSerializableAclExpression,
    ) as SerializableAclExpression;
  }
  if (typeof expression === 'function') {
    return expression();
  }
  return expression;
}

export function aclPermissionFactory(permission: string): AclFactory {
  const factory: AclFactory = (parameter?: string) =>
    parameter === undefined ? [permission] : [permission, parameter];

  factory.permission = [permission];
  return factory;
}

export class Acl {
  /** ****************** COMPONENTS ACCESS ******************* */

  /** **** Campaigns and Workspaces ***** */
  static CampaignRead: AclFactory = aclPermissionFactory('campaign.read');

  static CampaignUpdate: AclFactory = aclPermissionFactory('campaign.update');

  static CampaignFull: AclFactory = aclPermissionFactory('campaign.full');

  static CampaignAny = Acl.or(
    Acl.CampaignRead,
    Acl.CampaignUpdate,
    Acl.CampaignFull,
  );

  /** **** Results ***** */
  static DashboardRead: AclFactory = aclPermissionFactory('dashboard.read');
  static AnalyticsDashboardFull: AclFactory = aclPermissionFactory(
    'analyticsDashboard.full',
  );

  /** **** Feedback management ***** */
  static FeedbackRead: AclFactory = aclPermissionFactory('feedback.read');

  /** **** Flow ***** */
  static FlowRead: AclFactory = aclPermissionFactory('flow.read');

  static FlowDraft: AclFactory = aclPermissionFactory('flow.draft');

  static FlowUpdate: AclFactory = aclPermissionFactory('flow.update');

  static FlowFull: AclFactory = aclPermissionFactory('flow.full');

  /** **** Distribution ***** */
  static DistributionDashboardRead: AclFactory = aclPermissionFactory(
    'distributionDashboard.read',
  );

  static DistributionDashboardExport: AclFactory =
    aclPermissionFactory('export');

  /** Data triggers */
  static DataTriggerManualImport: AclFactory = aclPermissionFactory(
    'dataTrigger.manualImport',
  );

  static DataTriggerRead: AclFactory = aclPermissionFactory('dataTrigger.read');

  static DataTriggerUpdate: AclFactory =
    aclPermissionFactory('dataTrigger.update');

  static DataTriggerFull: AclFactory = aclPermissionFactory('dataTrigger.full');

  /** Link generators */
  static GeneratorLinksList: AclFactory = aclPermissionFactory(
    'generator.linksList',
  );

  static GeneratorRead: AclFactory = aclPermissionFactory('generator.read');

  static GeneratorUpdate: AclFactory = aclPermissionFactory('generator.update');

  static GeneratorFull: AclFactory = aclPermissionFactory('generator.full');

  /** Embedded widgets */
  static EmbeddedWidgetRead: AclFactory =
    aclPermissionFactory('embedWidget.read');

  static EmbeddedWidgetUpdate: AclFactory =
    aclPermissionFactory('embedWidget.update');

  static EmbeddedWidgetFull: AclFactory =
    aclPermissionFactory('embedWidget.full');

  static EmbeddedWidgetAny = Acl.or(
    Acl.EmbeddedWidgetRead,
    Acl.EmbeddedWidgetUpdate,
    Acl.EmbeddedWidgetFull,
  );

  /****** Personal account ******/
  static PersonalAccountUpdateName: AclFactory = aclPermissionFactory(
    'personalAccount.update.name',
  );
  static PersonalAccountUpdateImage: AclFactory = aclPermissionFactory(
    'personalAccount.update.image',
  );
  static PersonalAccountUpdateTimezone: AclFactory = aclPermissionFactory(
    'personalAccount.update.tz',
  );

  /** ****************** ADMINISTRATION ******************* */

  /** **** Team & Structure ***** */
  static AccountRead: AclFactory = aclPermissionFactory('account.read');
  static AccountUpdate: AclFactory = aclPermissionFactory('account.update');
  static AccountFull: AclFactory = aclPermissionFactory('account.full');
  static AccountAny = Acl.or(
    Acl.AccountRead,
    Acl.AccountUpdate,
    Acl.AccountFull,
  );

  static ProfileRead: AclFactory = aclPermissionFactory('profile.read');
  static ProfileUpdate: AclFactory = aclPermissionFactory('profile.update');
  static ProfileFull: AclFactory = aclPermissionFactory('profile.full');
  static ProfileAny = Acl.or(
    Acl.ProfileRead,
    Acl.ProfileUpdate,
    Acl.ProfileFull,
  );

  static TerminalRead: AclFactory = aclPermissionFactory('terminal.read');
  static TerminalUpdate: AclFactory = aclPermissionFactory('terminal.update');
  static TerminalFull: AclFactory = aclPermissionFactory('terminal.full');

  static CategoryRead: AclFactory = aclPermissionFactory('category.read');
  static CategoryUpdate: AclFactory = aclPermissionFactory('category.update');
  static CategoryFull: AclFactory = aclPermissionFactory('category.full');

  static CategoryAny = Acl.or(
    Acl.CategoryRead,
    Acl.CategoryUpdate,
    Acl.CategoryFull,
  );

  static CategoryUpdateOrFullAccess = Acl.or(
    Acl.CategoryUpdate,
    Acl.CategoryFull,
  );

  static StructureRead: AclFactory = aclPermissionFactory('structure.read');

  static StructureUpdate: AclFactory = aclPermissionFactory('structure.update');

  static StructureCreate: AclFactory = aclPermissionFactory('structure.create');

  static StructureDelete: AclFactory = aclPermissionFactory('structure.delete');

  /** **** Settings ***** */
  static WorkspaceRead: AclFactory = aclPermissionFactory('workspace.read');

  static WorkspaceUpdate: AclFactory = aclPermissionFactory('workspace.update');

  static WorkspaceFull: AclFactory = aclPermissionFactory('workspace.full');

  static AttributesRead: AclFactory = aclPermissionFactory('attributes.read');

  static AttributesUpdate: AclFactory =
    aclPermissionFactory('attributes.update');

  static AttributesFull: AclFactory = aclPermissionFactory('attributes.full');

  static NotificationRead: AclFactory =
    aclPermissionFactory('notification.read');

  static NotificationFull: AclFactory =
    aclPermissionFactory('notification.full');

  static NotificationUpdate: AclFactory = aclPermissionFactory(
    'notification.update',
  );

  static NotificationAny = Acl.or(
    Acl.NotificationRead,
    Acl.NotificationUpdate,
    Acl.NotificationFull,
  );

  static NotificationUpdateOrFullAccess = Acl.or(
    Acl.NotificationUpdate,
    Acl.NotificationFull,
  );

  static AutoreplyConditionRead: AclFactory = aclPermissionFactory(
    'autoreplyCondition.read',
  );
  static AutoreplyConditionUpdate: AclFactory = aclPermissionFactory(
    'autoreplyCondition.update',
  );
  static AutoreplyConditionFull: AclFactory = aclPermissionFactory(
    'autoreplyCondition.full',
  );

  static AutoreplyConditionAny = Acl.or(
    Acl.AutoreplyConditionRead,
    Acl.AutoreplyConditionUpdate,
    Acl.AutoreplyConditionFull,
  );

  static AutoreplyConditionUpdateOrFullAccess = Acl.or(
    Acl.AutoreplyConditionUpdate,
    Acl.AutoreplyConditionFull,
  );

  static TemplateRead: AclFactory = aclPermissionFactory('template.read');

  static TemplateUpdate: AclFactory = aclPermissionFactory('template.update');

  static TemplateFull: AclFactory = aclPermissionFactory('template.full');

  static TemplateEditOrFullAccess = Acl.or(
    Acl.TemplateUpdate,
    Acl.TemplateFull,
  );

  static LabelRead: AclFactory = aclPermissionFactory('label.read');

  static LabelUpdate: AclFactory = aclPermissionFactory('label.update');

  static LabelFull: AclFactory = aclPermissionFactory('label.full');

  static PredefinedAnswerRead: AclFactory = aclPermissionFactory(
    'predefinedAnswer.read',
  );
  static PredefinedAnswerUpdate: AclFactory = aclPermissionFactory(
    'predefinedAnswer.update',
  );
  static PredefinedAnswerFull: AclFactory = aclPermissionFactory(
    'predefinedAnswer.full',
  );

  static PredefinedAnswerAny = Acl.or(
    Acl.PredefinedAnswerRead,
    Acl.PredefinedAnswerUpdate,
    Acl.PredefinedAnswerFull,
  );

  static AutoreplyTemplateRead: AclFactory = aclPermissionFactory(
    'autoreplyTemplate.read',
  );

  static AutoreplyTemplateUpdate: AclFactory = aclPermissionFactory(
    'autoreplyTemplate.update',
  );

  static AutoreplyTemplateFull: AclFactory = aclPermissionFactory(
    'autoreplyTemplate.full',
  );

  static ChannelRead: AclFactory = aclPermissionFactory('channel.read');

  static ChannelUpdate: AclFactory = aclPermissionFactory('channel.update');

  static ChannelFull: AclFactory = aclPermissionFactory('channel.full');

  static DataSourceRead: AclFactory = aclPermissionFactory('dataSource.read');

  static DataSourceUpdate: AclFactory =
    aclPermissionFactory('dataSource.update');

  static DataSourceFull: AclFactory = aclPermissionFactory('dataSource.full');

  static ThrottlingRead: AclFactory = aclPermissionFactory('throttling.read');

  static ThrottlingUpdate: AclFactory =
    aclPermissionFactory('throttling.update');

  static ThrottlingFull: AclFactory = aclPermissionFactory('throttling.full');

  static ThrottlingUpdateOrFullAccess = Acl.or(
    Acl.ThrottlingFull(),
    Acl.ThrottlingUpdate(),
  );

  static RoleRead: AclFactory = aclPermissionFactory('role.read');

  static RoleUpdate: AclFactory = aclPermissionFactory('role.update');

  static RoleFull: AclFactory = aclPermissionFactory('role.full');

  static AuditLogRead: AclFactory = aclPermissionFactory('auditLog.read');

  static or(...operands: AclExpression[]): AclQuery {
    return ['||', ...operands.map((o) => (typeof o === 'function' ? o() : o))];
  }

  static and(...operands: AclExpression[]): AclQuery {
    return ['&&', ...operands.map((o) => (typeof o === 'function' ? o() : o))];
  }

  static not(...operands: AclExpression[]): AclQuery {
    return ['!', ...operands.map((o) => (typeof o === 'function' ? o() : o))];
  }
}
